import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { queueAdd, queueAll, queueRemove, queueCount, newClientId } from "@/lib/offline";

const OfflineContext = createContext(null);

export const OfflineProvider = ({ children }) => {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const busy = useRef(false);

  const refresh = useCallback(() => {
    queueCount().then(setPending).catch(() => {});
  }, []);

  const enqueue = useCallback(
    async (payload) => {
      const entry = { ...payload, client_id: payload.client_id || newClientId(), queued_at: new Date().toISOString() };
      await queueAdd(entry);
      refresh();
      return entry;
    },
    [refresh]
  );

  const sync = useCallback(async () => {
    if (busy.current || !navigator.onLine) return null;
    const entries = await queueAll();
    if (!entries.length) { refresh(); return null; }
    busy.current = true;
    setSyncing(true);
    try {
      const { data } = await api.post("/dpr/sync", { entries: entries.map(({ queued_at, ...e }) => e) });
      let created = 0;
      let rejected = 0;
      for (const r of data.results || []) {
        if (r.status === "rejected") { rejected += 1; }
        else { created += r.status === "created" ? 1 : 0; }
        await queueRemove(r.client_id);
        if (r.status === "rejected") toast.error(`Offline entry rejected: ${r.reason}`);
      }
      if (created) toast.success(`${created} offline DPR ${created === 1 ? "entry" : "entries"} synced`);
      refresh();
      return { created, rejected };
    } catch (e) {
      return null;
    } finally {
      busy.current = false;
      setSyncing(false);
    }
  }, [refresh]);

  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    refresh();
    if (navigator.onLine) sync();
    const t = setInterval(() => { if (navigator.onLine) sync(); }, 60000);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      clearInterval(t);
    };
  }, [sync, refresh]);

  return (
    <OfflineContext.Provider value={{ online, pending, syncing, enqueue, sync, refresh }}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () =>
  useContext(OfflineContext) || { online: true, pending: 0, syncing: false, enqueue: async () => {}, sync: async () => {}, refresh: () => {} };
